import { useMemo } from 'react';
import { calculateCheckoutPricing, type CheckoutPricing } from '../utils/pricing';
import type { DogFormData, Recipe } from '../types';
import type { AppConfigResponse } from '../types/config';

export interface UseCheckoutPricingArgs {
  formData: Partial<DogFormData>;
  recipes: Recipe[];
  config: AppConfigResponse;
  isTransition: boolean;
  normalizedSelectedRecipeIds: string[];
}

export interface CheckoutRecipeLine {
  productId: string;
  name: string;
  qty: number;
  originalTotal: number;
  discountedTotal: number;
  imageUrl?: string;
  proteinType?: string;
  benefits?: string[];
}

export interface UseCheckoutPricingResult extends CheckoutPricing {
  effectiveRecipeLines: CheckoutRecipeLine[];
  hasSupplements: boolean;
}

export function useCheckoutPricing({
  formData,
  recipes,
  config,
  isTransition,
  normalizedSelectedRecipeIds,
}: UseCheckoutPricingArgs): UseCheckoutPricingResult {
  const pricing = useMemo(
    () =>
      calculateCheckoutPricing({
        initiationPackDistribution: formData.initiationPackDistribution,
        initiationPackPrice: formData.initiationPackPrice,
        monthlyPlanPrice: formData.monthlyPlanPrice,
        supplementsOneOffTotal: formData.supplementsOneOffTotal,
        recipes,
        config,
        isInitiation: isTransition,
      }),
    [
      formData.initiationPackDistribution,
      formData.initiationPackPrice,
      formData.monthlyPlanPrice,
      formData.supplementsOneOffTotal,
      recipes,
      config,
      isTransition,
    ],
  );

  const effectiveRecipeLines = useMemo<CheckoutRecipeLine[]>(() => {
    if (pricing.initiationRecipeLines.length > 0) {
      return pricing.initiationRecipeLines;
    }
    if (normalizedSelectedRecipeIds.length === 0) return [];
    // normalizedSelectedRecipeIds are canonical product.ids (UUIDs). Match the
    // recipe / size variant by productId.
    return normalizedSelectedRecipeIds.map((productId) => {
      const recipe = recipes.find(
        (r) =>
          String(r.productId) === String(productId) ||
          (r.variants && Object.values(r.variants).some((v) => String(v.productId) === String(productId))),
      );
      const matchedVariantEntry = recipe?.variants
        ? Object.entries(recipe.variants).find(([, v]) => String(v.productId) === String(productId))
        : undefined;
      const matchedVariant = matchedVariantEntry?.[1];
      const sizeSuffix = matchedVariantEntry?.[0] ?? (recipe?.packSizeG ? `${recipe.packSizeG}g` : '');
      const unitPrice = matchedVariant?.priceEur ?? recipe?.pricePerUnit ?? 0;
      return {
        productId: productId,
        name: `${recipe?.name ?? `Receta ${productId}`}${sizeSuffix ? ` ${sizeSuffix}` : ''}`,
        qty: 1,
        originalTotal: unitPrice,
        discountedTotal: unitPrice,
        imageUrl: recipe?.imageUrl,
        proteinType: recipe?.proteinType,
        benefits: recipe?.benefits,
      };
    });
  }, [pricing.initiationRecipeLines, normalizedSelectedRecipeIds, recipes]);

  return {
    ...pricing,
    effectiveRecipeLines,
    hasSupplements: pricing.supplementsOneOffTotal > 0,
  };
}
